import { TemplateRef } from '@angular/core';
import { ZoneRegionsModels } from '@share-models';
import {
  CellTemplateContext,
  ColumnBuilder,
  DatasetTableAction,
  DatasetTableConfig,
  formattedColumn,
  TableConfigBuilder,
  deleteAction,
  editAction,
} from '@ui/lib/ui-kit/ui-dataset-table';

export interface CreateZoneRegionsTableConfigDependencies {
  processingIdsSet: () => Set<string>;
  countingCellTemplate: TemplateRef<CellTemplateContext<ZoneRegionsModels.ZoneRegionDocument>>;
  actionIds?: Array<'view' | 'edit' | 'delete'>;
}

export function createZoneRegionsTableConfig(
  dependencies: CreateZoneRegionsTableConfigDependencies,
): DatasetTableConfig<ZoneRegionsModels.ZoneRegionDocument> {
  const builder = new ColumnBuilder<ZoneRegionsModels.ZoneRegionDocument>();
  const configBuilder = new TableConfigBuilder<ZoneRegionsModels.ZoneRegionDocument>();

  const customViewActionConfig: DatasetTableAction<ZoneRegionsModels.ZoneRegionDocument> = {
    id: 'view',
    icon: 'ui-icon-annotation',
    iconType: 'ui-icon',
    label: 'ZONE_REGIONS.VIEW',
    tooltip: 'ZONE_REGIONS.VIEW',
    disabled: (row) => dependencies.processingIdsSet().has(row._id),
    showInline: true,
  };

  const editActionConfig = editAction<ZoneRegionsModels.ZoneRegionDocument>({
    label: 'ZONE_REGIONS.EDIT_ZONE_REGION',
    tooltip: 'ZONE_REGIONS.EDIT_ZONE_REGION',
    disabled: (row) => dependencies.processingIdsSet().has(row._id),
  });

  const deleteActionConfig = deleteAction<ZoneRegionsModels.ZoneRegionDocument>({
    tooltip: 'COMMON.DELETE',
    disabled: (row) => dependencies.processingIdsSet().has(row._id),
  });


  const actionIds = dependencies.actionIds ?? ['view', 'edit', 'delete'];
  const actions = [customViewActionConfig, editActionConfig, deleteActionConfig].filter((action) =>
    actionIds.includes(action.id as 'view' | 'edit' | 'delete'),
  );

  return configBuilder
    .columns([
      formattedColumn<ZoneRegionsModels.ZoneRegionDocument>(
        'name',
        'ZONE_REGIONS.TABLE.NAME',
        (_, row) => ZoneRegionsModels.getZoneRegionName(row) ?? '-',
        {
          width: 'minmax(200px, 1fr)',
          mobileVisible: true,
          cardMode: 'label-only',
        },
      ),
      builder
        .column('countingEnabled')
        .label('ZONE_REGIONS.TABLE.ZONE_COUNTING')
        .width('minmax(160px, 0.5fr)')
        .cellTemplate(dependencies.countingCellTemplate)
        .mobileVisible()
        .cardMode('label-only')
        .build(),
    ])
    .actions(actions)
    .defaultRowAction(customViewActionConfig.id)
    .actionsWidth(80)
    .rowHeight(52)
    .trackBy('_id')
    .build();
}
